import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { api, ApiError } from '../../lib/api';
import type { Answers, PublicForm } from '../../lib/types';
import { formatDateTime } from '../../lib/utils';
import { Spinner } from '../../components/ui';
import { FormRenderer } from './FormRenderer';

export function PublicFormPage() {
  const { slug = '' } = useParams();
  const qc = useQueryClient();
  const key = useMemo(() => ['public-form', slug], [slug]);

  const { data, error, isPending } = useQuery({
    queryKey: key,
    queryFn: () => api.get<PublicForm>(`/public/forms/${encodeURIComponent(slug)}`),
    retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (data) document.title = data.form.name;
  }, [data]);

  const saveDraft = useCallback(
    (answers: Answers) => api.put(`/public/forms/${encodeURIComponent(slug)}/draft`, { answers }),
    [slug],
  );

  const submit = useCallback(
    async (answers: Answers) => {
      const res = await api.post<{ submittedAt: string }>(
        `/public/forms/${encodeURIComponent(slug)}/submit`,
        { answers },
      );
      qc.setQueryData<PublicForm>(key, (old) =>
        old ? { ...old, status: 'submitted', submittedAt: res.submittedAt, answers } : old,
      );
    },
    [slug, qc, key],
  );

  return (
    <div className="public">
      {isPending && <Spinner label="Loading your form…" />}

      {error && (
        <div className="form-shell">
          <div className="card center-card">
            <h1>
              {error instanceof ApiError && error.status === 410 ? 'Link closed' : 'Form not found'}
            </h1>
            <p className="muted">
              {error instanceof ApiError && error.status < 500
                ? error.message
                : 'We could not load this form. Please try again in a moment.'}
            </p>
            <p className="muted">
              If you think this is a mistake, contact the person who sent you this link.
            </p>
          </div>
        </div>
      )}

      {data?.status === 'submitted' && (
        <div className="form-shell">
          <div className="card center-card">
            <div className="success-icon" aria-hidden>
              ✓
            </div>
            <h1>Thank you, {data.clientName}!</h1>
            <p>Your answers to “{data.form.name}” have been received.</p>
            <p className="muted">
              Submitted {formatDateTime(data.submittedAt)}. You can close this page.
            </p>
          </div>
        </div>
      )}

      {data && data.status !== 'submitted' && (
        <FormRenderer
          form={data.form}
          clientName={data.clientName}
          initialAnswers={data.answers}
          slug={data.slug}
          onSaveDraft={saveDraft}
          onSubmit={submit}
        />
      )}

      <footer className="public-footer muted">
        Your answers are saved automatically as you type.
      </footer>
    </div>
  );
}
